import { GENERAL_SECTION } from "../fields";

const SECTION_CATALOG = [
    { key: "scope", label: "Scope of the Clinical Evaluation" },
    {
        key: "device",
        label: "Device Overview",
        documentsLabel: "Select Documents applicable",
        description:
            "Choose the documents that can be used as source material for this section. All of them are searched co-equally unless a priority order is set below.",
    },
    {
        key: "non_clinical",
        label: "Non-Clinical and/or Pre-Clinical Data",
        description:
            "Choose the documents that can be used as source material for this section. At least one document is required. All of them are searched co-equally unless a priority order is set below.",
    },
    {
        key: "clinical",
        label: "Clinical Investigations",
        documentsLabel: "Select Documents applicable",
        description:
            "Choose the documents that can be used as source material for this section. All of them are searched co-equally unless a priority order is set below.",
    },
    {
        key: "sota",
        label: "Clinical Background, Current Knowledge and State-of-the-Art",
        articlesLabel: "List of Articles",
        articlesDescription: "List of Literature articles.",
    },
    {
        key: "literature",
        label: "Published Scientific Literature on DUE",
        articlesLabel: "Documents for this section",
        articlesDescription:
            "Choose the literature documents to be used for this section. This section requires at least one file.",
    },
];

const ORDERED_SECTION_KEY = "literature";

const SECTION_ARTICLE_TYPE = {
    sota: "sota",
    literature: "slr",
};

const SECTIONS_WITHOUT_DOCUMENTS = new Set(["sota", "literature"]);

const STEPS = [
    { key: "general", title: "Device Information" },
    { key: "sections", title: "Select Sections" },
    { key: "details", title: "Configure" },
];

const BLOCK_TITLES = {
    "general:info": "General Info",
    "general:core": "Device and Regulatory Facts",
};

const BLOCK_SECTIONS = {};

const BLOCK_DESCRIPTIONS = {};

const NOT_YET_LAUNCHED = { field: "already_launched", value: false };
const EQUIVALENT_DEVICE_USED = { field: "equivalent_device_available", value: true };

const PER_DEVICE = "device_names";
const PER_EQUIVALENT_DEVICE = "equivalent_device_names";

const FREE_TEXT_DATE_PLACEHOLDER = "e.g. Mar-24, Q2 2020";
const RANGE_START_PLACEHOLDER = "e.g. 01-Jan-20";
const RANGE_END_PLACEHOLDER = "e.g. 31-Dec-25";

const FIELDS = [
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "device_names",
        label: "Device Name(s)",
        type: "string_list",
        description:
            "Enter the name of the subject device. Add all applicable device names if multiple devices are included.",
        placeholder: "e.g. Vascular Device",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "manufacturer",
        label: "Manufacturer",
        type: "text",
        description: "Name of the manufacturer of the subject device(s)",
        placeholder: "e.g. MedTech Solutions Ltd.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "intended_purpose",
        label: "Intended Purpose",
        type: "long_text",
        perItemOf: PER_DEVICE,
        everyItem: true,
        description:
            "Describe the intended medical purpose, target population, and use environment of each device.",
        placeholder:
            "e.g. Facilitates access to the peripheral vasculature and supports the delivery of medical devices during diagnostic or interventional procedures.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "additional_intended_use",
        label: "Additional Intended Use",
        type: "long_text",
        perItemOf: PER_DEVICE,
        description:
            "Use this when the Intended Purpose is short or leaves out details. Add the fuller intended use, which provides details such as the medical conditions treated, the indications, the target patients or the use setting.",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "device_description",
        label: "Device Description",
        type: "long_text",
        perItemOf: PER_DEVICE,
        description:
            "Describe what the device is, its main parts or materials, how it works and how it is used.",
        placeholder:
            "e.g. A sterile, single-use plastic syringe made of a barrel, plunger and rubber stopper, used with a needle to inject fluids into or withdraw fluids from the body.",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "device_class",
        label: "Device Class",
        type: "text",
        description:
            "Specify the classification or category assigned to the product under the applicable regulatory framework.",
        placeholder: "e.g. Class III",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "device_family_type",
        label: "Device Family Type",
        type: "text",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "date_format",
        label: "Date Format",
        type: "select",
        options: ["dd-mm-yyyy", "dd/mm/yyyy", "mm/dd/yyyy", "DD Month YYYY"],
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "evaluation_start_date",
        row: "cer_date_range",
        label: "CER Date Range Start",
        type: "text",
        placeholder: RANGE_START_PLACEHOLDER,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "evaluation_end_date",
        row: "cer_date_range",
        label: "CER Date Range End",
        type: "text",
        placeholder: RANGE_END_PLACEHOLDER,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "already_launched",
        label: "Device Already Launched",
        type: "boolean",
        description: "Indicate whether the device has already been commercially launched.",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "launch_date",
        label: "Device Launch Date",
        type: "text",
        perItemOf: PER_DEVICE,
        placeholder: FREE_TEXT_DATE_PLACEHOLDER,
        description: "Enter the launch date of each device.",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "launch_country",
        label: "Country of Device Launch",
        type: "text",
        perItemOf: PER_DEVICE,
        description: "Enter the country or countries of launch for each device.",
        placeholder: "e.g. Germany, Europe",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "launch_date_eu",
        label: "Launch Date in EU",
        type: "text",
        perItemOf: PER_DEVICE,
        placeholder: FREE_TEXT_DATE_PLACEHOLDER,
        description: "Enter the launch date in the EU for each device.",
        hiddenIf: NOT_YET_LAUNCHED,
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        name: "ce_mark_date_mdr",
        label: "MDR CE Mark Date",
        type: "date",
        description: "Date the device received its CE mark under the MDR.",
    },
    {
        section: GENERAL_SECTION,
        block: "info",
        group: "sota",
        name: "sota_locale",
        wireName: "locale",
        label: "Language",
        type: "select",
        options: [
            { value: "UK", label: "English (UK)" },
            { value: "US", label: "English (US)" },
        ],
        description:
            "Select the language and regional format to be used for spelling, terminology, and document conventions.",
    },

    {
        section: GENERAL_SECTION,
        block: "core",
        name: "first_evaluation_report",
        label: "First Clinical Evaluation Report for Device",
        type: "boolean",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "technical_file_number",
        label: "Technical File Number",
        type: "text",
        description: "Enter the technical documentation file reference number.",
        placeholder: "e.g. TF-2457-REV03",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "cep_document_number",
        label: "Clinical Evaluation Plan (CEP) ID",
        type: "text",
        description:
            "Enter the identifier of the Clinical Evaluation Plan associated with this CER.",
        placeholder: "e.g. CEP-2025-014",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "last_evaluation_date",
        label: "Date of Last Clinical Evaluation",
        type: "text",
        placeholder: FREE_TEXT_DATE_PLACEHOLDER,
        description: "Enter the completion date of the previous clinical evaluation.",
        hiddenIf: NOT_YET_LAUNCHED,
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "last_evaluation_data_through",
        label: "Last Clinical Evaluation Included Data Until",
        type: "text",
        placeholder: FREE_TEXT_DATE_PLACEHOLDER,
        description:
            "Enter the cut-off date of the data covered by the previous clinical evaluation.",
        hiddenIf: NOT_YET_LAUNCHED,
    },

    {
        section: "scope",
        name: "sales_start_date",
        row: "sales_period",
        label: "Sales Period",
        type: "text",
        description: "Enter the start and end dates of the sales period covered by this report.",
        placeholder: RANGE_START_PLACEHOLDER,
    },
    {
        section: "scope",
        name: "sales_end_date",
        row: "sales_period",
        label: "Sales Period End",
        hideLabel: true,
        type: "text",
        placeholder: RANGE_END_PLACEHOLDER,
    },
    {
        section: "scope",
        name: "total_units",
        label: "Total Units of Device",
        type: "number",
        description: "Enter the total number of device units sold during the reporting period.",
        placeholder: "e.g. 250000",
    },
    {
        section: "scope",
        name: "total_sales_units",
        label: "Overall Sales Units Since Launch",
        type: "number",
        description:
            "Enter the cumulative number of device units sold since the device was first launched.",
        placeholder: "e.g. 150000",
    },
    {
        section: "scope",
        name: "development_stage",
        label: "Stage of Development",
        type: "text",
        description: "Enter the current development stage of the device.",
        placeholder: "e.g. Verification and Validation",
        visibleIf: NOT_YET_LAUNCHED,
    },
    {
        section: "scope",
        name: "years_until_launch",
        label: "Years Within Which Device Will Be Tentatively Launched",
        type: "number",
        precision: 0,
        min: 0,
        description:
            "Enter the number of years within which the device is expected to be launched.",
        placeholder: "e.g. 2",
        visibleIf: NOT_YET_LAUNCHED,
    },
    {
        section: "scope",
        name: "estimated_launch_date",
        label: "Tentative Launch Date",
        type: "text",
        placeholder: "e.g. Q2 2027",
        description: "Enter the planned or expected launch date.",
        visibleIf: NOT_YET_LAUNCHED,
    },

    {
        section: "non_clinical",
        name: "biocompatibility_tests",
        label: "BioCompatibility Test",
        type: "clinical_entry_list",
        description:
            "Provide a complete list of biocompatibility reports applicable to the device. (Name/Device/Source Document)",
    },
    {
        section: "non_clinical",
        name: "non_clinical_tests",
        label: "Non-Clinical Tests",
        type: "clinical_entry_list",
        description:
            "When provided this is the exhaustive list — the agent extracts metadata for exactly these tests instead of discovering them.",
    },

    {
        section: "clinical",
        name: "equivalent_device_available",
        label: "Equivalent Device Available",
        type: "boolean",
        description: "Indicate whether clinical data from an equivalent device is used.",
    },
    {
        section: "clinical",
        name: "equivalent_device_names",
        label: "Equivalent Device Name(s)",
        type: "string_list",
        description: "Enter the name of each equivalent device.",
        placeholder: "e.g. Vascular Access System",
        requiredIf: EQUIVALENT_DEVICE_USED,
        visibleIf: EQUIVALENT_DEVICE_USED,
    },
    {
        section: "clinical",
        name: "equivalent_manufacturers",
        label: "Equivalent Device Manufacturer(s)",
        type: "text",
        perItemOf: PER_EQUIVALENT_DEVICE,
        description: "Enter the manufacturer of each equivalent device.",
        placeholder: "e.g. MedTech Solutions Ltd.",
        requiredIf: EQUIVALENT_DEVICE_USED,
        visibleIf: EQUIVALENT_DEVICE_USED,
    },
    {
        section: "clinical",
        name: "clinical_study_titles",
        label: "Clinical Study Titles",
        type: "clinical_entry_list",
        description:
            "Recommended: List the study titles to improve accuracy and traceability. If left blank, studies will be identified automatically from the selected documents.",
    },

    {
        section: "sota",
        group: "sota",
        name: "sota_device_type",
        wireName: "device_type",
        label: "Device Type",
        type: "text",
        required: true,
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_indications",
        wireName: "indications",
        label: "Indications",
        type: "long_text",
        required: true,
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_alternative_techniques",
        wireName: "alternative_techniques",
        label: "Alternative Techniques",
        type: "string_list",
        description:
            "List alternative therapies, procedures, or technologies that may be discussed in the State of the Art section.",
        placeholder: "e.g. Coronary Artery Bypass Grafting (CABG)",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_competitors",
        wireName: "competitors",
        label: "Competitors",
        type: "competitor_list",
        incompleteMessage: "Enter a device name for every competitor, or remove the row.",
        description:
            "List the manufacturer and name of each competitor device that should be considered during benchmark and comparative analysis. If not provided, competitor comparison content will not be generated.",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_clinical_benchmark_metrics",
        wireName: "clinical_benchmark_metrics",
        label: "Clinical Benchmark Metrics",
        type: "comma_list",
        description:
            "Provide clinical performance metrics that should be used when generating benchmarks and acceptance criteria. Separate multiple metrics with commas.",
        placeholder: "e.g. Restenosis Rate, Target Lesion Failure",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_sota_status",
        wireName: "sota_status",
        label: "State-of-the-Art Status",
        type: "text",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_is_ddc",
        wireName: "is_ddc",
        label: "Drug Device Combination",
        type: "boolean",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_review_start_date",
        wireName: "review_start_date",
        row: "sota_search_window",
        label: "Search Window",
        type: "text",
        placeholder: "e.g. 01-Jan-20",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_review_end_date",
        wireName: "review_end_date",
        row: "sota_search_window",
        label: "Search Window End",
        hideLabel: true,
        type: "text",
        placeholder: "e.g. 31-Dec-25",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_total_articles",
        wireName: "total_articles",
        label: "Total Articles Screened",
        type: "text",
        description:
            "Enter the total number of records screened during the literature review process.",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_number_of_sota_articles",
        wireName: "number_of_sota_articles",
        label: "Articles Selected for Detailed Analysis",
        type: "text",
        description:
            "Enter the number of articles included for full-text review and detailed clinical assessment.",
    },
    {
        section: "sota",
        group: "sota",
        name: "sota_previous_cer_number_of_sota_articles_included",
        wireName: "previous_cer_number_of_sota_articles_included",
        label: "Articles Carried Forward From the Previous CER",
        type: "text",
        description:
            "Enter the number of previously accepted literature articles retained from the prior CER.",
    },

    {
        section: "literature",
        name: "disease_condition",
        label: "Disease or Condition",
        type: "text",
        description: "Enter the disease or condition the device is intended to diagnose or treat.",
        placeholder: "e.g. Malocclusion",
    },
    {
        section: "literature",
        name: "literature_search_databases",
        label: "Literature Search Databases",
        type: "string_list",
        description: "List the databases used for the literature search, e.g. Embase, PubMed.",
        placeholder: "e.g. PubMed",
    },
    {
        section: "literature",
        name: "literature_search_date_range",
        label: "Literature Search Date Range",
        type: "text_range",
        placeholders: [`Start (${RANGE_START_PLACEHOLDER})`, `End (${RANGE_END_PLACEHOLDER})`],
        description:
            "Specify the date range covered by the literature search. This period will be used when describing the literature review methodology.",
    },
];

const NON_METADATA_KEYS = new Set(["device_family_type"]);

const advisories = (sectionKey, values) => {
    const out = [];

    if (sectionKey === "clinical") {
        const titles = values.clinical_study_titles;
        if (!(titles && titles.length)) {
            out.push({
                type: "warning",
                message:
                    "Clinical Investigations will be generated only when at least one study title is listed.",
            });
        }
        if (values.equivalent_device_available === true) {
            out.push({
                type: "warning",
                field: "clinical_study_titles",
                message: "Add the device name to each study title",
                description:
                    "This is how studies on the subject device are told apart from studies on the equivalent device. A study counts as an equivalent device study only when its Device matches an Equivalent Device Name exactly. Every other study, including one with no device, is treated as a subject device study.",
            });
        }
    }

    if (sectionKey === "sota") {
        const techniques = values.sota_alternative_techniques;
        if (!(techniques && techniques.filter((t) => t && t.trim()).length)) {
            out.push({
                type: "warning",
                field: "sota_alternative_techniques",
                message: "If not provided, the related output section will remain empty.",
            });
        }
    }

    return out;
};

export default {
    id: "cer",
    steps: STEPS,
    sectionCatalog: SECTION_CATALOG,
    fields: FIELDS,
    blockTitles: BLOCK_TITLES,
    blockSections: BLOCK_SECTIONS,
    blockDescriptions: BLOCK_DESCRIPTIONS,
    sectionArticleType: SECTION_ARTICLE_TYPE,
    sectionsWithoutDocuments: SECTIONS_WITHOUT_DOCUMENTS,
    optionalSections: new Set(),
    orderedSectionKey: ORDERED_SECTION_KEY,
    nonMetadataKeys: NON_METADATA_KEYS,
    fixedSections: null,
    payloadTables: ["sota"],
    emitsDeviceFamily: true,
    hasArticles: true,
    advisories,
};
